import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BrandMark } from "@/components/BrandMark";
import { ArrowRight, Clapperboard, Play, Music, Palette, Radio, Gamepad2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const STAGE_ICON = { video: Play, audio: Music, art: Palette, show: Radio, game: Gamepad2 };
import { GuestWantForm } from "@/components/GuestWantForm";

const shell = "relative h-[var(--feed-h,calc(100dvh-64px))] md:h-auto md:min-h-[420px] w-full snap-start snap-always overflow-hidden flex flex-col justify-center p-8 md:rounded-2xl";

export function SloganCard({ slogan, index }) {
  return (
    <section className={`${shell} bg-[#FF3B30] border-y md:border border-[#FF3B30]`} data-testid={`feed-slogan-card-${index}`}>
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-black/20 blur-3xl" />
      <div className="relative">
        <BrandMark />
        <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.6 }} transition={{ duration: 0.5 }}
          className="mt-4 font-black tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-white uppercase">{slogan.big}</motion.h2>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.25, duration: 0.5 }}
          className="mt-4 text-base md:text-lg text-white/85 max-w-md">{slogan.small}</motion.p>
        <Link to={slogan.to} data-testid={`feed-slogan-cta-${index}`} className="mt-8 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-black text-white text-sm font-bold hover:bg-neutral-900 transition-colors">
          {slogan.cta} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

export function WantCard({ index }) {
  return (
    <section className={`${shell} bg-[#121212] border-y md:border border-[#007AFF]/40`} data-testid={`feed-want-card-${index}`}>
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] font-bold">Can't find it?</div>
      <h2 className="mt-3 font-black tracking-tight text-3xl sm:text-4xl text-zinc-50 leading-tight">Tell sellers what you want.</h2>
      <p className="mt-2 text-sm text-zinc-400 max-w-md">Post it in one tap — sellers who have it get alerted and the match lands in your inbox.</p>
      <div className="mt-6"><GuestWantForm compact /></div>
    </section>
  );
}

export function StageCard({ index, posts, onOpen }) {
  const { user } = useAuth();
  const cta = user?.role === "seller" || user?.role === "admin" ? ["/seller/stage", "Post on your Stage"] : user ? ["/select-role", "Open your own Stage"] : ["/register", "Open your own Stage"];
  return (
    <section className={`${shell} bg-[#0B0B0D] border-y md:border border-[#FF9F0A]/50`} data-testid={`feed-stage-card-${index}`}>
      <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#FF9F0A]/10 blur-3xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#FF9F0A] font-bold"><Clapperboard className="h-3.5 w-3.5" /> On Stage now</div>
        <h2 className="mt-3 font-black tracking-tight leading-tight text-2xl sm:text-3xl text-zinc-50">{posts?.length ? <>Stores performing live.<br /><span className="text-zinc-400">Watch, play, then shop.</span></> : <>Every store gets a Stage.<br /><span className="text-zinc-400">Go live, perform, exhibit, host games — then sell.</span></>}</h2>
        {!posts?.length && <p className="mt-3 text-sm text-zinc-400 max-w-md">Videos, music, exhibitions, live shows and audience games — your first Hosting is free, and so is your first live session. <Link to="/stages" className="text-[#FF9F0A] underline underline-offset-2" data-testid={`feed-stage-browse-${index}`}>Browse the Stages</Link></p>}
        {posts?.length > 0 && <div className="mt-5 grid grid-cols-3 gap-2">
          {posts.slice(0, 3).map((p) => (
            <button key={p.id} type="button" onClick={() => onOpen(p)} data-testid={`feed-stage-post-${p.id}`} className="group relative aspect-[4/5] overflow-hidden rounded-xl border border-white/10 bg-[#161616] text-left">
              {p.cover_url ? <img src={p.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                : (() => { const I = STAGE_ICON[p.type] || Play; return <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#2a1b1b] to-[#111]"><I className="h-10 w-10 text-white/25" /></div>; })()}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-2">
                <div className="text-[9px] uppercase tracking-wider text-[#FF9F0A] font-bold">{p.type === "show" && p.live_state === "live" ? "● Live" : p.type}</div>
                <div className="text-xs font-bold leading-tight line-clamp-2">{p.title}</div>
                <div className="text-[10px] text-zinc-400 truncate">{p.seller_name}</div>
              </div>
            </button>
          ))}
        </div>}
        <Link to={cta[0]} data-testid={`feed-stage-cta-${index}`} className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#FF9F0A] text-black text-sm font-bold hover:bg-[#ffb340] transition-colors">
          {cta[1]} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}
